/**
 * English copy
 * Purpose: English locale pack (en)
 * Usage: registered in i18n/index.js; read via t('key')
 */
export default {
  common: {
    brandLogoAlt: 'Brand logo',
    home: 'Home',
    cases: 'Case Studies',
    contact: 'Contact Us',
    getQuote: 'Get a Quote',
    backHome: 'Back to Home',
    email: 'Email',
    phone: 'Phone',
    loading: 'Loading…',
    loadFailed: 'Failed to load data. Please make sure the API server is running.'
  },
  nav: {
    homeAria: 'Back to home'
  },
  media: {
    video: 'Video',
    empty: 'No media available',
    videoUnsupported: 'Your browser does not support video playback.',
    videoThumb: 'Switch to video',
    imageThumb: 'Switch to image'
  },
  home: {
    title: 'Find quality suppliers. Source with confidence.',
    subtitle:
      'Browse curated product images, then open details for specs, lead times and customization options.',
    browseProducts: 'Browse Products',
    galleryTitle: 'Product Gallery',
    galleryHint: 'Click any product image to view details',
    filterLabel: 'Category',
    filterAll: 'All',
    filterEmpty: 'No products in this category. Try another one.'
  },
  categories: {
    industrial: 'Industrial Goods',
    home: 'Home Goods',
    sportsOutdoor: 'Sports & Outdoor',
    kitchen: 'Kitchenware',
    electronics: 'Consumer Electronics',
    office: 'Office Supplies',
    toys: 'Toys & Gifts',
    bags: 'Bags & Accessories',
    tableware: 'Tableware Gifts',
    lighting: 'Lighting',
    sportsProtection: 'Sports Protection',
    daily: 'Daily Essentials'
  },
  product: {
    backList: '← Back to products',
    moq: 'MOQ: ',
    material: 'Material: ',
    leadTime: 'Lead time: ',
    inquire: 'Ask About Sourcing',
    keepBrowsing: 'Keep Browsing',
    notFoundTitle: 'Product not found',
    notFoundDesc: 'This product may be unavailable, or the link is invalid.'
  },
  contact: {
    title: 'Contact Us',
    subtitle:
      'It all starts with hello. Tell us what you need to source or customize — we’ll get back to you soon.',
    infoTitle: 'Contact Info',
    officeLabel: 'China Office',
    officeAddress: 'Huashan Town, Huadu District, Guangzhou, Guangdong, China',
    emailValue: "2017emilyli{'@'}gmail.com",
    whatsappLabel: 'WhatsApp',
    whatsappValue: '18666080334',
    workHoursLabel: 'Business Hours',
    workHours: 'Mon–Fri 09:00 – 18:00 (GMT+8)',
    formTitle: 'Send an Inquiry',
    formTip: 'Fill in the form below and we’ll reply as soon as possible.',
    name: 'Name *',
    namePlaceholder: 'Your name',
    company: 'Company',
    companyPlaceholder: 'Company name (optional)',
    emailLabel: 'Email *',
    phoneLabel: 'Phone',
    phonePlaceholder: 'A number we can reach you on',
    help: 'How can we help? *',
    helpPlaceholder:
      'Describe product category, estimated quantity, lead time or other needs',
    wantCall: 'I’d like to schedule a call',
    submit: 'Submit Inquiry',
    submitting: 'Submitting…',
    errorRequired: 'Please complete all required fields.',
    errorEmail: 'Please enter a valid email address.',
    success: 'Thanks! We’ve received your message and will contact you soon.',
    fail: 'Submission failed. Please try again later.'
  },
  footer: {
    desc: 'We connect you with quality suppliers to make sourcing and customization simpler.',
    quickLinks: 'Quick Links',
    contactTitle: 'Contact',
    emailLine: "Email: 2017emilyli{'@'}gmail.com",
    whatsappLine: 'WhatsApp: 18666080334',
    addressLine: 'Address: Huashan Town, Huadu District, Guangzhou, Guangdong, China',
    rights: 'All rights reserved.'
  },
  lang: {
    label: 'Language',
    zh: '中文',
    en: 'English',
    es: 'Español'
  },
  meta: {
    home: 'Products',
    productDetail: 'Product Details',
    cases: 'Case Studies',
    caseDetail: 'Case Detail',
    contact: 'Contact Us',
    admin: 'Admin Dashboard',
    adminLogin: 'Admin Login'
  },
  cases: {
    title: 'Real Sourcing Case Studies',
    subtitle:
      'See how we help clients across industries with sourcing, sampling, QC and delivery — results first.',
    filterLabel: 'Case Category',
    filterAll: 'All',
    filterEmpty: 'No cases in this category. Try another one.',
    viewDetail: 'View details',
    backList: '← Back to case studies',
    challenge: 'The Challenge',
    solution: 'Our Approach',
    result: 'The Outcome',
    highlights: 'Key Highlights',
    cta: 'Discuss a Similar Project',
    moreCases: 'More Cases',
    notFoundTitle: 'Case not found',
    notFoundDesc: 'This case may be unavailable, or the link is invalid.',
    metrics: {
      saving: 'Cost saving',
      units: 'Units delivered',
      leadTime: 'Lead time'
    },
    items: {
      c1: {
        title: 'Bluetooth Speaker OEM for Cross-border E-commerce',
        client: 'North America e-commerce brand · Consumer Electronics',
        summary:
          'Delivered redesign, firmware customization and first shipment in 8 weeks, cutting total sourcing cost by about 22%.',
        challenge:
          'The previous supplier had unstable lead times and could not support small-batch firmware updates before peak season.',
        solution:
          'We shortlisted three export-ready factories, compared samples with third-party testing, and locked tooling tweaks, packaging and vessel schedule with weekly QC checkpoints.',
        result:
          'First order of 85,000 units shipped on time with defect rate under 0.8%. The client later made us their exclusive China sourcing partner.',
        highlights: [
          'IPX5 waterproof and battery life testing',
          'OEM look and firmware customization',
          'Pre-shipment AQL inspection process'
        ]
      },
      c2: {
        title: 'Silicone Kitchen Gift Sets for Retail',
        client: 'European retail chain · Kitchenware',
        summary:
          'Custom multi-color kitchen gift sets for the holiday season, with multilingual manuals and shock-proof export packaging.',
        challenge:
          'Strict color consistency and unboxing standards, plus DE/FR/EN manuals, all tied to a Christmas sailing window.',
        solution:
          'Selected a food-grade certified silicone factory, confirmed color boards and pilot runs, then booked space early with double-layer protective packing.',
        result:
          '120,000 gift sets delivered on schedule with damage rate below 0.3%. The client renewed the next holiday series.',
        highlights: [
          'Food-grade materials and export certificates',
          'Trilingual manuals and custom gift boxes',
          'On-time holiday delivery'
        ]
      },
      c3: {
        title: 'Industrial Toolbox Cost Replacement',
        client: 'Australian industrial distributor · Industrial Goods',
        summary:
          'Replaced high-cost imported toolboxes with reinforced local production, achieving around 31% cost reduction.',
        challenge:
          'Existing European/US products were too expensive; the client needed equal durability plus private-label printing.',
        solution:
          'Benchmarked the original structure, optimized hinges and latches, ran load/drop tests, and customized EVA dividers with logo printing.',
        result:
          '12,500 sets delivered successfully. End-user reorder rates improved while unit cost dropped significantly versus imports.',
        highlights: [
          'Passed load and drop tests',
          'Private-label printing supported',
          'Clear savings vs imported option'
        ]
      },
      c4: {
        title: 'Home Storage Boxes for Marketplace Rollout',
        client: 'Southeast Asia DTC brand · Home Goods',
        summary:
          'Built a multi-size storage SKU matrix with gift-box packaging and barcode labeling for rapid marketplace launch.',
        challenge:
          'Six size combinations had to go live quickly while meeting platform packaging and barcode compliance rules.',
        solution:
          'Planned a shared mold family to reduce tooling, aligned packaging/barcode rules, and shipped in two waves to lower inventory risk.',
        result:
          'Over 200,000 units delivered with marketplace ratings above 4.7. We were listed as a core annual supplier.',
        highlights: [
          'Multi-size SKUs launched fast',
          'Compliant gift boxes and barcodes',
          'Staged shipments cut inventory risk'
        ]
      },
      c5: {
        title: 'Co-branded Sports Bottle Launch',
        client: 'LATAM sports brand · Sports & Outdoor',
        summary:
          'Delivered co-brand print, capacity confirmation and export compliance to support a new-market launch.',
        challenge:
          'High print accuracy for the collab artwork, food-contact compliance, and limited appetite for first-order risk.',
        solution:
          'Used food-grade Tritan, confirmed print proofs and color, prepared compliance docs, and advised a split-SKU pilot order.',
        result:
          '60,000 units delivered on time; 70% of the collab SKU sold in week one, followed by a reorder with new colors.',
        highlights: [
          'Food-grade compliant delivery',
          'High-precision co-brand printing',
          'Fast reorder after launch'
        ]
      },
      c6: {
        title: 'Ceramic Dinnerware Gift Boxes for Export',
        client: 'Canadian gift company · Tableware Gifts',
        summary:
          'Custom glaze and pattern dinnerware gift sets with shock-proof packaging for long ocean freight.',
        challenge:
          'Fragile ceramics plus long transit historically caused high damage rates; glaze had to match brand visuals.',
        solution:
          'Upgraded inner trays and carton stacking strength, ran simulated shipping tests, and locked glaze after two approval rounds.',
        result:
          '48,000 sets cleared and listed with warehouse damage under 0.5%. The client reordered for the next season.',
        highlights: [
          'Custom glaze and artwork',
          'Shock-proof packing cut damage',
          'Stable holiday channel supply'
        ]
      }
    }
  },
  products: {
    p1: {
      name: 'Industrial Toolbox Set',
      category: 'Industrial Goods',
      moq: '500 sets',
      material: 'ABS + Steel',
      leadTime: '15–25 days',
      description:
        'Multi-spec toolboxes for factories and maintenance. Customizable dividers, logo printing and colors for bulk orders.',
      specs: ['Reinforced structure', 'Customizable liner', 'OEM/ODM ready']
    },
    p2: {
      name: 'Home Storage Box Series',
      category: 'Home Goods',
      moq: '1000 pcs',
      material: 'PP plastic',
      leadTime: '12–20 days',
      description:
        'Lightweight durable storage for home use. Multiple sizes, color packaging and barcode labeling for e-commerce.',
      specs: ['Multiple sizes', 'Stackable design', 'Custom gift boxes']
    },
    p3: {
      name: 'Outdoor Sports Bottle',
      category: 'Sports & Outdoor',
      moq: '2000 pcs',
      material: 'Food-grade Tritan',
      leadTime: '18–28 days',
      description:
        'Lightweight leak-proof bottle for cross-border and brand customization. Color, capacity, print and packaging options.',
      specs: ['Food-grade material', 'Leak-proof seal', 'Brand print ready']
    },
    p4: {
      name: 'Silicone Kitchen Utensil Set',
      category: 'Kitchenware',
      moq: '800 sets',
      material: 'Food-grade silicone',
      leadTime: '20–30 days',
      description:
        'Heat-resistant silicone utensils for retail and gifting. Color mixes, accessory counts and manuals can be customized.',
      specs: ['Heat resistant', 'Non-stick friendly', 'Bundle options']
    },
    p5: {
      name: 'Portable Bluetooth Speaker',
      category: 'Consumer Electronics',
      moq: '500 units',
      material: 'ABS + Fabric',
      leadTime: '25–35 days',
      description:
        'Portable speaker with OEM look and firmware options. Support for sampling, QC and export packaging.',
      specs: ['IPX5 waterproof', 'Custom battery life', 'Firmware customization']
    },
    p6: {
      name: 'Desktop Organizer',
      category: 'Office Supplies',
      moq: '1000 pcs',
      material: 'Metal + Wood',
      leadTime: '15–22 days',
      description:
        'Minimal desk organizer for office and home. Size tweaks, finishes and brand plaques available.',
      specs: ['Stable load-bearing', 'Minimal look', 'Custom plaque']
    },
    p7: {
      name: 'Educational Building Blocks',
      category: 'Toys & Gifts',
      moq: '600 sets',
      material: 'Eco-friendly ABS',
      leadTime: '22–30 days',
      description:
        'Export-ready building blocks with safety testing support and packaging design. Theme and multilingual manuals available.',
      specs: ['Export safety compliant', 'Theme customization', 'Multilingual manuals']
    },
    p8: {
      name: 'Travel Packing Cubes Set',
      category: 'Bags & Accessories',
      moq: '1200 sets',
      material: 'Nylon / Polyester',
      leadTime: '14–21 days',
      description:
        'Lightweight packing cubes with color and size mixes. Ideal for e-commerce bestsellers and co-branded packaging.',
      specs: ['Light & durable', 'Multi-piece sets', 'Custom zipper colors']
    },
    p9: {
      name: 'Ceramic Dinnerware Gift Box',
      category: 'Tableware Gifts',
      moq: '400 sets',
      material: 'Ceramic',
      leadTime: '25–40 days',
      description:
        'Gift-boxed ceramic dinnerware for holidays and retail. Custom glazes, patterns and shock-proof export packaging.',
      specs: ['Gift packaging', 'Custom glaze', 'Shock-proof export pack']
    },
    p10: {
      name: 'LED Ambient Light',
      category: 'Lighting',
      moq: '800 units',
      material: 'PC + Aluminum alloy',
      leadTime: '20–30 days',
      description:
        'Home ambient lighting with customizable color temperature and controls. Sampling, QC and bulk shipment support.',
      specs: ['Multiple color temps', 'Remote/APP options', 'Custom appearance']
    },
    p11: {
      name: 'Sports Knee Support',
      category: 'Sports Protection',
      moq: '1500 pcs',
      material: 'Neoprene / Nylon',
      leadTime: '16–24 days',
      description:
        'Protective sports gear with size matrix and packaging options. Fit for cross-border and sports brand OEM.',
      specs: ['Elastic & breathable', 'Multiple sizes', 'Custom hang tags']
    },
    p12: {
      name: 'Stainless Steel Tumbler',
      category: 'Daily Essentials',
      moq: '1000 pcs',
      material: '304 stainless steel',
      leadTime: '18–26 days',
      description:
        'Double-wall vacuum tumbler with coating, laser logo and gift box options. Ideal for corporate gifts and retail.',
      specs: ['Vacuum insulation', 'Laser logo ready', 'Gift box optional']
    }
  }
}
